import {
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  InputAdornment,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getNewChoicesList } from "../../../../services/choices/choices_services";
import { useQuery } from "react-query";
import locales from "../../../../constants/locales";
import { SearchRounded } from "@mui/icons-material";
import styled from "@emotion/styled";
import { black, purple, purpleLight } from "../../../../theme/colors";
import { useSurveyorsFilterStore } from "../../../../store/use-surveyors-filter-store";
import Loading from "../../../../components/loading/loading";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import SortIcon from "../../../../assets/sort.svg";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import SortSurveysPopup, {
  DEFAULT_SURVEYS_SORT_OPTION,
  SurveysSortOption,
  surveysSortOptionLabel,
  surveysSortOptionToSortOrder,
} from "../../../choices/components/sort_surveys_popup/sort_surveys_popup";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

interface ChoiceListItem {
  id: number;
  name: string;
  languages: string[];
  validFrom: string;
  validTo: string;
  updatedAt: string;
  createdAt: string;
  totalQuestions: number;
  choiceId: number;
}

export default function SurveyorChoicesListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const { surveysPage, setSurveysPage } = useSurveyorsFilterStore();
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [sortOption, setSortOption] = useState<SurveysSortOption>(DEFAULT_SURVEYS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const navigate = useNavigate();

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setSurveysPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setSurveysPage(newPage + 1);
    setSearchParams({ page: (newPage + 1).toString(), currentTab: searchParams.get("currentTab") || "1" }, { replace: true });
  };

  const { data: choicesListData, isLoading } = useQuery(
    ["choicesListing", surveysPage, pageSize, debouncedSearchValue, sortOption],
    async () => {
      let response = await getNewChoicesList({
        keyword: debouncedSearchValue,
        page: surveysPage,
        size: pageSize,
        dateFrom: "",
        dateTo: "",
        validFrom: "",
        validTo: "",
        sortOrder: surveysSortOptionToSortOrder(sortOption),
      });
      return response?.data?.data || [];
    }
  );
  const list: ChoiceListItem[] = choicesListData?.list || [];
  const startRange = (surveysPage - 1) * pageSize + 1;
  const endRange = Math.min(surveysPage * pageSize, choicesListData?.total || 0);
  const customText = `Showing ${choicesListData?.total ? startRange : 0} to ${endRange || 0} of ${choicesListData?.total || 0} entries`;

  useEffect(() => {
    setSearchParams({ page: surveysPage.toString(), currentTab: "1" }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search Surveys"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: "text.primary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <ToolbarButton
              icon={SortIcon}
              label={surveysSortOptionLabel(sortOption)}
              onClick={() => setSortDialogOpen(true)}
            />
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : list.length === 0 ? (
          <TableEmptyState message="No surveys found" hint="Try adjusting your search." />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.3 },
                { label: "Survey ID", flex: 0.4 },
                { label: "Survey", flex: 1.9 },
                { label: "Valid From", flex: 0.7 },
                { label: "Valid To", flex: 0.7 },
                { label: "Actions" },
              ]}
              lastColumnWidth={180}
              align="left"
              bordered
            />
            {list.map((choice, index) => (
              <TableRow key={choice.id} bordered>
                <TableCell label="Sl. No." flex={0.3} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {(surveysPage - 1) * pageSize + index + 1}
                  </Typography>
                </TableCell>
                <TableCell label="Survey ID" flex={0.4} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {choice?.id}
                  </Typography>
                </TableCell>
                <TableCell label="Survey" flex={1.9} bordered>
                  <Typography fontWeight={600} color={black} noWrap>
                    {choice?.name}
                  </Typography>
                  <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" mt={0.25} gap={0.75}>
                    <Typography variant="body2" color={purple} fontWeight={600}>
                      {`Questions: ${choice.totalQuestions}`}
                    </Typography>
                    {choice?.languages?.map((item: string) => (
                      <Chip
                        key={item}
                        size="small"
                        label={locales?.find((locale) => locale?.code === item)?.name || item}
                        sx={{ bgcolor: purpleLight, color: purple, fontWeight: 600, textTransform: "capitalize" }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell label="Valid From" flex={0.7} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {choice?.validFrom || "-"}
                  </Typography>
                </TableCell>
                <TableCell label="Valid To" flex={0.7} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {choice?.validTo || "-"}
                  </Typography>
                </TableCell>
                <TableCell isLast lastColumnWidth={180} bordered>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/surveyors/viewSurveys/${choice?.id}`)}
                      sx={{
                        borderColor: purple,
                        color: purple,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: purple, bgcolor: purpleLight },
                      }}
                    >
                      View responses
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {list.length > 0 && (
        <CardActions sx={{ px: 3, pb: 3, mt: "auto" }}>
          <Box width="100%">
            <TableFooterPagination
              count={choicesListData?.total || 0}
              page={surveysPage - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(newSize) => {
                setPageSize(newSize);
                setSurveysPage(1);
              }}
            />
          </Box>
        </CardActions>
      )}

      <SortSurveysPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
    </>
  );
}
